import { __awaiter, __generator } from "tslib";
import SockJS from "sockjs-client";
var EventBus = /** @class */ (function () {
    function EventBus(url, options) {
        this.pingInterval = 5000;
        this.options = {};
        this.pingTimerID = null;
        this.reconnectEnabled = false;
        this.reconnectAttempts = 0;
        this.reconnectTimerID = null;
        this.maxReconnectAttempts = Infinity;
        this.reconnectDelayMin = 1000;
        this.reconnectDelayMax = 5000;
        this.reconnectExponent = 2;
        this.randomizationFactor = 0.5;
        this.defaultHeaders = null;
        this.url = "";
        this.state = null;
        this.status = {
            CONNECTING: "CONNECTING",
            OPEN: "OPEN",
            CLOSING: "CLOSING",
            CLOSED: "CLOSED"
        };
        this.initializeConnection = function (url) {
            if (url === void 0) { url = this.url; }
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    console.log(url);
                    this.setupSockJSConnection(url, this.options).then(console.log);
                    return [2 /*return*/, this.setupSockJSConnection(url, this.options)];
                });
            });
        };
        this.getReconnectDelay = function () {
            var ms = this.reconnectDelayMin *
                Math.pow(this.reconnectExponent, this.reconnectAttempts);
            if (this.randomizationFactor) {
                var rand = Math.random();
                var deviation = Math.floor(rand * this.randomizationFactor * ms);
                ms =
                    (Math.floor(rand * 10) & 1) === 0
                        ? ms - deviation
                        : ms + deviation;
            }
            return Math.min(ms, this.reconnectDelayMax) | 0;
        };
        // default event handlers
        this.onerror = function (err) {
            try {
                console.error(err);
            }
            catch (e) {
                // dev tools are disabled so we cannot use console on IE
            }
        };
        this.onevent = function (event, message) {
            return false; // return false to signal that this message is not processed
        };
        this.onunhandled = function (json) {
            try {
                if (json.type === "err")
                    this.onerror(json);
                else if (json.event) {
                    console.warn("No handler found for event: %o. Message: %O", json.event, json);
                }
                else {
                    console.warn("No handler found for message: ", json);
                }
            }
            catch (e) {
                // dev tools are disabled so we cannot use console on IE
            }
        };
        this.setupSockJSConnection = function (url, options) {
            return __awaiter(this, void 0, void 0, function () {
                var _this_1 = this;
                return __generator(this, function (_a) {
                    return [2 /*return*/, new Promise(function (resolve, reject) {
                            var _this = _this_1;
                            _this.sockJSConn = new SockJS(url, null, options);
                            _this.state = _this.status.CONNECTING;
                            // handlers and reply handlers are tied to the state of the socket
                            // they are added onopen or when sending, so reset when reconnecting
                            _this.handlers = {};
                            _this.replyHandlers = {};
                            _this.sockJSConn.onopen = function () {
                                console.log("onopen");
                                _this.enablePing(true);
                                _this.state = _this.status.OPEN;
                                _this.onopen && _this.onopen();
                                if (_this.reconnectTimerID) {
                                    _this.reconnectAttempts = 0;
                                    // fire separate event for reconnects
                                    // consistent behavior with adding handlers onopen
                                    _this.onreconnect && _this.onreconnect();
                                }
                                resolve();
                            };
                            _this.sockJSConn.onclose = function (e) {
                                _this.state = _this.status.CLOSED;
                                if (_this.pingTimerID)
                                    clearInterval(_this.pingTimerID);
                                if (_this.reconnectEnabled &&
                                    _this.reconnectAttempts < _this.maxReconnectAttempts) {
                                    _this.sockJSConn = null;
                                    // set id so users can cancel
                                    _this.reconnectTimerID = setTimeout(_this.setupSockJSConnection, _this.getReconnectDelay());
                                    ++_this.reconnectAttempts;
                                }
                                _this.onclose && _this.onclose(e);
                                reject();
                            };
                            _this.sockJSConn.onmessage = function (e) {
                                console.log("onmessage", e);
                                var json;
                                try {
                                    json = JSON.parse(e.data);
                                }
                                catch (ex) {
                                    json = {
                                        type: "err",
                                        failureType: ex.toString(),
                                        message: e.data
                                    };
                                }
                                // define a reply function on the message itself
                                if (json.replyAddress) {
                                    Object.defineProperty(json, "reply", {
                                        value: function (message, headers, callback) {
                                            _this.send(json.replyAddress, message, headers, callback);
                                        }
                                    });
                                }
                                if (_this.handlers[json.address]) {
                                    // iterate all registered handlers
                                    var handlers = _this.handlers[json.address];
                                    for (var i = 0; i < handlers.length; i++) {
                                        if (json.type === "err") {
                                            handlers[i]({
                                                failureCode: json.failureCode,
                                                failureType: json.failureType,
                                                message: json.message
                                            });
                                        }
                                        else {
                                            handlers[i](null, json);
                                        }
                                    }
                                }
                                else if (_this.replyHandlers[json.address]) {
                                    // Might be a reply message
                                    var handler = _this.replyHandlers[json.address];
                                    delete _this.replyHandlers[json.address];
                                    if (json.type === "err") {
                                        handler({
                                            failureCode: json.failureCode,
                                            failureType: json.failureType,
                                            message: json.message
                                        });
                                    }
                                    else {
                                        handler(null, json);
                                    }
                                }
                                else {
                                    if (!json.event ||
                                        !_this.onevent(json.event, json.message)) {
                                        _this.onunhandled(json);
                                    }
                                }
                            };
                        })];
                });
            });
        };
        /**
         * Unregister a handler
         *
         * @param {String} address
         * @param {Object} [headers]
         * @param {Function} callback
         */
        this.unregisterHandler = function (address, headers, callback) {
            // are we ready?
            if (this.state !== this.status.OPEN) {
                throw new Error("INVALID_STATE_ERR");
            }
            var handlers = this.handlers[address];
            if (handlers) {
                if (typeof headers === "function") {
                    callback = headers;
                    headers = {};
                }
                var idx = handlers.indexOf(callback);
                if (idx !== -1) {
                    handlers.splice(idx, 1);
                    if (handlers.length === 0) {
                        // No more local handlers so we should unregister the connection
                        this.sockJSConn.send(JSON.stringify({
                            type: "unregister",
                            address: address,
                            headers: mergeHeaders(this.defaultHeaders, headers)
                        }));
                        delete this.handlers[address];
                    }
                }
            }
            close;
        };
        /**
         * Send a message
         *
         * @param {String} address
         * @param {Object} message
         * @param {Object} [headers]
         * @param {Function} [callback]
         */
        this.send = function (address, message, headers, callback) {
            // are we ready?
            if (this.state !== this.status.OPEN) {
                throw new Error("INVALID_STATE_ERR");
            }
            if (typeof headers === "function") {
                callback = headers;
                headers = {};
            }
            var envelope = {
                type: "send",
                address: address,
                headers: mergeHeaders(this.defaultHeaders, headers),
                body: message,
                replyAddress: ""
            };
            if (callback) {
                var replyAddress = makeUUID();
                envelope.replyAddress = replyAddress;
                this.replyHandlers[replyAddress] = callback;
            }
            this.sockJSConn.send(JSON.stringify(envelope));
        };
        /**
         * Publish a message
         *
         * @param {String} address
         * @param {Object} message
         * @param {Object} [headers]
         */
        this.publish = function (address, message, headers) {
            // are we ready?
            if (this.state !== this.status.OPEN) {
                throw new Error("INVALID_STATE_ERR");
            }
            this.sockJSConn.send(JSON.stringify({
                type: "publish",
                address: address,
                headers: mergeHeaders(this.defaultHeaders, headers),
                body: message
            }));
        };
        /**
         * Register a new handler
         *
         * @param {String} address
         * @param {Object} [headers]
         * @param {Function} callback
         */
        this.registerHandler = function (address, headers, callback) {
            // are we ready?
            if (this.state !== this.status.OPEN) {
                throw new Error("INVALID_STATE_ERR");
            }
            if (typeof headers === "function") {
                callback = headers;
                headers = {};
            }
            // ensure it is an array
            if (!this.handlers[address]) {
                this.handlers[address] = [];
                // First handler for this address so we should register the connection
                this.sockJSConn.send(JSON.stringify({
                    type: "register",
                    address: address,
                    headers: mergeHeaders(this.defaultHeaders, headers)
                }));
            }
            this.handlers[address].push(callback);
        };
        /**
         * Closes the connection to the EventBus Bridge,
         * preventing any reconnect attempts
         */
        this.close = function () {
            this.state = this.status.CLOSING;
            this.enableReconnect(false);
            this.sockJSConn.close();
        };
        this.enablePing = function (enable) {
            var _this_1 = this;
            if (enable) {
                var sendPing = function () {
                    _this_1.sockJSConn.send(JSON.stringify({ type: "ping" }));
                };
                if (this.pingInterval > 0) {
                    // Send the first ping then send a ping every pingInterval milliseconds
                    sendPing();
                    this.pingTimerID = setInterval(sendPing, this.pingInterval);
                }
            }
            else {
                if (this.pingTimerID) {
                    clearInterval(this.pingTimerID);
                    this.pingTimerID = null;
                }
            }
        };
        this.enableReconnect = function (enable) {
            this.reconnectEnabled = enable;
            if (!enable && this.reconnectTimerID) {
                clearTimeout(this.reconnectTimerID);
                this.reconnectTimerID = null;
                this.reconnectAttempts = 0;
            }
        };
        if (options) {
            this.options = options;
            this.pingInterval = options.vertxbus_ping_interval || 5000;
            this.pingTimerID = null;
            this.reconnectEnabled = false;
            this.reconnectAttempts = 0;
            this.reconnectTimerID = null;
            this.maxReconnectAttempts =
                options.vertxbus_reconnect_attempts_max || Infinity;
            this.reconnectDelayMin =
                options.vertxbus_reconnect_delay_min || 1000;
            this.reconnectDelayMax =
                options.vertxbus_reconnect_delay_max || 5000;
            this.reconnectExponent = options.vertxbus_reconnect_exponent || 2;
            this.randomizationFactor =
                options.vertxbus_randomization_factor || 0.5;
            this.defaultHeaders = null;
        }
        if (url) {
            this.url = url;
        }
    }
    return EventBus;
}());
export { EventBus };
export function makeUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (a, b) {
        return ((b = Math.random() * 16),
            (a == "y" ? (b & 3) | 8 : b | 0).toString(16));
    });
}
function mergeHeaders(defaultHeaders, headers) {
    if (defaultHeaders) {
        if (!headers) {
            return defaultHeaders;
        }
        for (var headerName in defaultHeaders) {
            if (defaultHeaders.hasOwnProperty(headerName)) {
                // user can overwrite the default headers
                if (typeof headers[headerName] === "undefined") {
                    headers[headerName] = defaultHeaders[headerName];
                }
            }
        }
    }
    // headers are required to be a object
    return headers || {};
}
export default EventBus;
